/**
 * Attacks (02 §5): Hidegons, Drunk and Mighta (state handler D_E9FD: the attack logic D_EDCD, then
 * either the walker L_EA08 or the wind-up D_ED3B) and the Invader's laser (the start of D_1E9F).
 *
 * D_EDCD: once the cooldown has run out, a grounded monster tries 1 in 4 an update; its player must
 * be ahead of it (it faces the player) and level with it - or above it, 1 in 32 - and a software
 * slot must be free. It then stands still for a 7-update wind-up (D_ED3B) and the shot leaves 2
 * columns ahead (L_ED41), unless a wall fills that cell. Hidegons and Mighta walk on after one more
 * update; Drunk stands until its bottle comes back (L_ED37, L12DE).
 */

import { playerX, playerY } from './chase';
import { hit, probe } from './map';
import { initialProjectileMotion } from './projectileState';
import { ATTACK, LASER, SHOT_BASE_X, SHOT_BASE_Y, SHOT_SLOTS } from './tuning';
import { walkerUpdate } from './walker';
import { rngOf } from '../random';
import type { Monster, PlayerId, Projectile, ProjectileKind, World } from '../types';

/**
 * Is one of the 16 slots a monster's shot may take free (L_EE25, $1EC0)? The C64 shares them with
 * the bubbles; a wind-up holds its slot (type $42) until the shot leaves.
 */
function slotFree(world: World): boolean {
  const held = world.monsters.filter((m) => m.ai.reserved).length;
  return world.bubbles.length + world.projectiles.length + held < SHOT_SLOTS;
}

/** A new shot in cell (col, row), flying `dir`, from monster `m`. */
export function addShot(
  world: World, m: Monster, kind: ProjectileKind, col: number, row: number, dir: 1 | -1,
): Projectile {
  const motion = { ...initialProjectileMotion(), col: col & 0xff, row: row & 0xff, home: col & 0xff };
  const p: Projectile = {
    id: world.nextId++, kind, x: (col * 8 + SHOT_BASE_X) & 0xff, y: (row * 8 + SHOT_BASE_Y) & 0xff, dir, owner: m.id,
    motion,
  };
  world.projectiles.push(p);
  world.events.push({ type: 'attack', monsterId: m.id, kind });
  return p;
}

/** D_EDCD: the cooldown, then the decision. Leaves `attack` at 7 when the wind-up begins. */
function decide(world: World, m: Monster, target: PlayerId): void {
  const a = m.ai;
  if (a.attack >= 0) return;
  if (a.cooldown > 0) {
    a.cooldown = (a.cooldown - 1) & 0xff;
    if (a.cooldown !== 0) return;
  }
  if (a.jump >= 0 || a.falling) return;
  const rng = rngOf(world);
  if (rng.int(ATTACK.chance) !== 0) return;
  const px = playerX(world, target);
  // `sbc FA,y` runs with the random generator's carry: level X goes either way.
  const playerLeft = m.x > px || (m.x === px && rng.int(2) === 1);
  const facingRight = a.frame < a.faceFrames;
  if (playerLeft ? facingRight || m.x < ATTACK.minX : !facingRight || m.x >= ATTACK.maxX) return;
  const py = playerY(world, target);
  if (m.y < py) return;
  if (m.y > py && rng.int(ATTACK.lowerChance) !== 0) return;
  if (!slotFree(world)) return;
  a.reserved = true;
  a.attack = ATTACK.windUp;
  a.cooldown = ATTACK.cooldown;
}

/**
 * L_ED41: the shot leaves, 2 columns ahead in the monster's row, if its two cells beside the lower
 * body are open (cell pointer + $2B/$2C facing right, + $27/$28 facing left); else the slot is let go.
 */
function letGo(world: World, m: Monster): void {
  const a = m.ai;
  a.reserved = false;
  const right = a.frame < a.faceFrames;
  const p = probe(m);
  const open = right ? !hit(world, p, 0x2b) && !hit(world, p, 0x2c) : !hit(world, p, 0x27) && !hit(world, p, 0x28);
  if (!open) return;
  const col = ((m.x - 0x14) & 0xff) >> 3;
  const row = ((m.y - 0x15) & 0xff) >> 3;
  const shotCol = col + (right ? ATTACK.ahead : -ATTACK.ahead);
  const kind: ProjectileKind = m.kind === 'mighta' ? 'rock' : m.kind === 'drunk' ? 'bottle' : 'fireball';
  const shot = addShot(world, m, kind, shotCol, row, right ? 1 : -1);
  // L_EDB9: the fireball's picture starts at its way ($80 & $7F = 0 left, 2 right); the others at 0.
  if (kind === 'fireball') shot.motion.anim = right ? 2 : 0;
  // L_EDAA: Drunk waits for its bottle (its cooldown byte holds the slot, and is not counted meanwhile).
  if (kind === 'bottle') a.attack = ATTACK.drunkWait;
}

/** D_ED3B: one update of the wind-up (or of Drunk's wait): standing still, no animation. */
function windUp(world: World, m: Monster): void {
  const a = m.ai;
  a.attack--;
  if (a.attack !== 0) return;
  // L_ED37: nothing held (Drunk waiting for its bottle) - back to 1, to come round again.
  if (!a.reserved) {
    a.attack = 1;
    return;
  }
  letGo(world, m);
}

/** D_E9FD: the state handler of Hidegons, Drunk and Mighta. */
export function attackerUpdate(world: World, m: Monster, target: PlayerId): void {
  decide(world, m, target);
  if (m.ai.attack < 0) walkerUpdate(world, m, target);
  else windUp(world, m);
}

/**
 * D_1E9F, before the Invader runs: once its cooldown is out, 12 in 256 an update, standing (not
 * falling), no laser of its own out, above Y $A8, and a slot free: a laser drops from under its feet.
 */
export function invaderLaser(world: World, m: Monster): void {
  const a = m.ai;
  if (a.cooldown > 0) {
    a.cooldown = (a.cooldown - 1) & 0xff;
    if (a.cooldown !== 0) return;
  }
  if (rngOf(world).int(256) >= LASER.chance) return;
  if (a.falling || a.attack >= 0 || m.y >= LASER.maxY || !slotFree(world)) return;
  const col = ((m.x - LASER.fromX) & 0xff) >> 3;
  const row = ((m.y - LASER.fromY) & 0xff) >> 3;
  addShot(world, m, 'laser', col, row, m.facing);
  a.cooldown = LASER.cooldown;
  a.attack = LASER.cooldown;
}
